import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Users,
  Briefcase,
  Package,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Building2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  tenantAdminService,
  PlatformAuditLog,
} from '@/features/branding/services/tenant-admin.service';

export const Route = createFileRoute('/system/support')({
  component: SupportPage,
});

const EVENT_TYPE_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'tenant.created', label: 'tenant.created' },
  { value: 'subscription.assigned', label: 'subscription.assigned' },
  { value: 'subscription.updated', label: 'subscription.updated' },
  { value: 'subscription.credits_updated', label: 'subscription.credits_updated' },
  { value: 'addon.activated', label: 'addon.activated' },
  { value: 'addon.cancelled', label: 'addon.cancelled' },
  { value: 'feature_flag.override_set', label: 'feature_flag.override_set' },
  { value: 'feature_flag.admin_bypass', label: 'feature_flag.admin_bypass' },
];

const EVENT_COLORS: Record<string, string> = {
  'tenant.created': 'bg-emerald-900 text-emerald-300',
  'subscription.assigned': 'bg-blue-900 text-blue-300',
  'subscription.updated': 'bg-blue-900 text-blue-300',
  'subscription.credits_updated': 'bg-cyan-900 text-cyan-300',
  'addon.activated': 'bg-purple-900 text-purple-300',
  'addon.cancelled': 'bg-orange-900 text-orange-300',
  'addon.expired': 'bg-red-900 text-red-300',
  'feature_flag.override_set': 'bg-yellow-900 text-yellow-300',
  'feature_flag.override_removed': 'bg-yellow-900 text-yellow-300',
  'feature_flag.admin_bypass': 'bg-red-900 text-red-300',
  'system_user.created': 'bg-indigo-900 text-indigo-300',
  'system_user.updated': 'bg-indigo-900 text-indigo-300',
  'system_user.deleted': 'bg-red-900 text-red-300',
};

const PAGE_SIZE = 30;

// ─── Linha de log expansível ──────────────────────────────────────────────────

function LogRow({ log }: { log: PlatformAuditLog }) {
  const [expanded, setExpanded] = useState(false);
  const hasPayload = log.payload && Object.keys(log.payload).length > 0;
  const badgeClass = EVENT_COLORS[log.eventType] ?? 'bg-gray-800 text-gray-300';

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  return (
    <>
      <tr
        className={`border-b border-gray-800 hover:bg-gray-800/40 transition-colors ${hasPayload ? 'cursor-pointer' : ''}`}
        onClick={() => hasPayload && setExpanded((v) => !v)}
      >
        <td className="py-2 pr-2 pl-3 w-4">
          {hasPayload ? (
            expanded ? (
              <ChevronDown className="h-3 w-3 text-gray-600" />
            ) : (
              <ChevronRight className="h-3 w-3 text-gray-600" />
            )
          ) : null}
        </td>
        <td className="py-2 pr-3 text-xs text-gray-500 font-mono whitespace-nowrap">
          {formatDate(log.createdAt)}
        </td>
        <td className="py-2 pr-3">
          <span className={`text-xs px-1.5 py-0.5 rounded font-mono font-medium ${badgeClass}`}>
            {log.eventType}
          </span>
        </td>
        <td className="py-2 pr-3 text-xs">
          <span
            className={`px-1.5 py-0.5 rounded text-xs ${
              log.actorType === 'system_user'
                ? 'bg-indigo-900/40 text-indigo-400'
                : 'bg-gray-800 text-gray-500'
            }`}
          >
            {log.actorType === 'system_user' ? 'system' : 'tenant'}
          </span>
        </td>
      </tr>
      {expanded && hasPayload && (
        <tr className="bg-gray-950 border-b border-gray-800">
          <td colSpan={4} className="py-2 pl-10 pr-4">
            <pre className="text-xs text-gray-300 overflow-x-auto max-h-36 font-mono leading-relaxed">
              {JSON.stringify(log.payload, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Painel do tenant selecionado ─────────────────────────────────────────────

function TenantSupportPanel({ tenantId }: { tenantId: string }) {
  const [eventFilter, setEventFilter] = useState('all');
  const [page, setPage] = useState(0);

  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ['tenant-health', tenantId],
    queryFn: () => tenantAdminService.getTenantHealth(tenantId),
    staleTime: 30_000,
  });

  const { data: logs = [], isLoading: logsLoading, refetch, isFetching } = useQuery({
    queryKey: ['tenant-support-logs', tenantId, eventFilter, page],
    queryFn: () =>
      tenantAdminService.getTenantAuditLogs(tenantId, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        eventType: eventFilter !== 'all' ? eventFilter : undefined,
      }),
  });

  if (healthLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-500 text-sm">
        Carregando...
      </div>
    );
  }

  if (!health) return null;

  const hasIntegrityIssues =
    health.integrity.usersWithoutRole > 0 || health.integrity.orphanedPermissions > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-white">{health.tenantName}</h2>
            <Badge
              className={`text-xs ${health.active ? 'bg-emerald-900 text-emerald-300' : 'bg-red-900 text-red-300'}`}
            >
              {health.active ? 'Ativo' : 'Inativo'}
            </Badge>
          </div>
          <p className="text-xs text-gray-500 font-mono mt-0.5">
            slug: {health.tenantSlug} · id: {tenantId}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-gray-500 hover:text-white gap-1.5"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Usuários', value: health.counts.users, icon: Users, color: 'text-blue-400' },
          { label: 'Clientes', value: health.counts.clients, icon: Briefcase, color: 'text-emerald-400' },
          { label: 'Times', value: health.counts.teams, icon: Users, color: 'text-purple-400' },
          { label: 'Ativos', value: health.counts.assets, icon: Package, color: 'text-orange-400' },
          { label: 'Roles', value: health.counts.roles, icon: Shield, color: 'text-indigo-400' },
          { label: 'Senhas', value: health.counts.passwords, icon: Shield, color: 'text-gray-400' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-gray-800 rounded-lg p-3 flex items-center gap-2">
            <Icon className={`h-3.5 w-3.5 ${color} flex-shrink-0`} />
            <div className="min-w-0">
              <p className="text-lg font-bold text-white leading-none">{value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Integridade */}
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
          hasIntegrityIssues
            ? 'bg-yellow-900/30 border border-yellow-800 text-yellow-300'
            : 'bg-emerald-900/20 border border-emerald-900 text-emerald-400'
        }`}
      >
        {hasIntegrityIssues ? (
          <>
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            <span>
              {health.integrity.usersWithoutRole > 0 &&
                `${health.integrity.usersWithoutRole} usuário(s) sem role · `}
              {health.integrity.orphanedPermissions > 0 &&
                `${health.integrity.orphanedPermissions} permissão(ões) órfãs`}
            </span>
          </>
        ) : (
          <>
            <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
            <span>Sem problemas de integridade detectados</span>
          </>
        )}
      </div>

      {/* Logs */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
            Histórico de ações
          </p>
          <Select
            value={eventFilter}
            onValueChange={(v) => { setEventFilter(v); setPage(0); }}
          >
            <SelectTrigger className="w-44 h-7 text-xs bg-gray-800 border-gray-700 text-gray-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-gray-100">
              {EVENT_TYPE_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className="text-xs font-mono focus:bg-gray-700"
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-0">
            {logsLoading ? (
              <p className="text-center text-gray-500 py-8 text-sm">Carregando logs...</p>
            ) : logs.length === 0 ? (
              <p className="text-center text-gray-600 py-8 text-sm">Nenhuma ação registrada</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700 text-gray-600 text-xs">
                      <th className="pb-2 pl-3 w-4" />
                      <th className="text-left pb-2 pr-3">Timestamp</th>
                      <th className="text-left pb-2 pr-3">Evento</th>
                      <th className="text-left pb-2 pr-3">Ator</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <LogRow key={log.id} log={log} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {(logs.length === PAGE_SIZE || page > 0) && (
          <div className="flex items-center justify-between mt-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="text-xs text-gray-500 hover:text-white h-7 disabled:opacity-30"
            >
              ← Anterior
            </Button>
            <span className="text-xs text-gray-600">Pág. {page + 1}</span>
            <Button
              variant="ghost"
              size="sm"
              disabled={logs.length < PAGE_SIZE}
              onClick={() => setPage((p) => p + 1)}
              className="text-xs text-gray-500 hover:text-white h-7 disabled:opacity-30"
            >
              Próxima →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

function SupportPage() {
  const [search, setSearch] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['system-tenants'],
    queryFn: () => tenantAdminService.listTenants(),
  });

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return tenants;
    return tenants.filter(
      (t) =>
        t.tenantName.toLowerCase().includes(q) ||
        t.tenantSlug.toLowerCase().includes(q),
    );
  }, [tenants, search]);

  return (
    <div className="flex h-[calc(100vh-0px)] overflow-hidden">
      {/* Lista de tenants (sidebar esquerda) */}
      <aside className="w-72 flex-shrink-0 border-r border-gray-800 flex flex-col bg-gray-900">
        <div className="p-4 border-b border-gray-800">
          <h1 className="text-base font-bold text-white mb-3">Suporte</h1>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
            <Input
              placeholder="Buscar tenant..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 bg-gray-800 border-gray-700 text-gray-100 placeholder:text-gray-600 text-sm h-8"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <p className="text-center text-gray-600 text-sm py-8">Carregando...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-600 text-sm py-8">Nenhum tenant encontrado</p>
          ) : (
            <ul className="p-2 space-y-0.5">
              {filtered.map((tenant) => (
                <li key={tenant.tenantId}>
                  <button
                    onClick={() => setSelectedTenantId(tenant.tenantId)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors group ${
                      selectedTenantId === tenant.tenantId
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <Building2
                          className={`h-3.5 w-3.5 flex-shrink-0 ${
                            selectedTenantId === tenant.tenantId
                              ? 'text-indigo-200'
                              : 'text-gray-500 group-hover:text-gray-400'
                          }`}
                        />
                        <span className="text-sm font-medium truncate">{tenant.tenantName}</span>
                      </div>
                      <span
                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ml-2 ${
                          tenant.active ? 'bg-emerald-400' : 'bg-red-500'
                        }`}
                      />
                    </div>
                    <p
                      className={`text-xs font-mono mt-0.5 pl-5 truncate ${
                        selectedTenantId === tenant.tenantId ? 'text-indigo-300' : 'text-gray-600'
                      }`}
                    >
                      {tenant.tenantSlug}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-3 border-t border-gray-800">
          <p className="text-xs text-gray-600 text-center">
            {tenants.length} tenant{tenants.length !== 1 ? 's' : ''} no total
          </p>
        </div>
      </aside>

      {/* Painel direito */}
      <main className="flex-1 overflow-y-auto p-6 bg-gray-950">
        {!selectedTenantId ? (
          <div className="flex flex-col items-center justify-center h-full text-center gap-3">
            <Building2 className="h-12 w-12 text-gray-700" />
            <p className="text-gray-500 text-sm">
              Selecione um tenant na lista ao lado para ver os detalhes de suporte
            </p>
          </div>
        ) : (
          <TenantSupportPanel key={selectedTenantId} tenantId={selectedTenantId} />
        )}
      </main>
    </div>
  );
}
